import { useCallback, useState } from 'react';
import useStores from 'hooks/use-stores.hooks';
import { EventNames, track } from 'utils/analytics.utils';
import type { VcsIntegrationEvent } from 'components/templates/analytics';
import type { GitProviderTypes } from 'types/api.types';
import type { TokenTypes } from 'types/api.types';
import { useAsyncPromise } from 'hooks/use-async-promise';
import type { VCSForm } from 'components/templates/templates-wizard/common/template-wizard.types';
import { getInitialGitProvider, isGitProviderUsingToken } from 'utils/vcs.utils';
import toUpper from 'lodash/toUpper';

export type AuthType = TokenTypes;

const ALIGNMENT_PROVIDERS: GitProviderTypes[] = ['BitBucket', 'GitLab', 'AzureDevOps'];

export const providerSupportingOauthIntegration: Set<GitProviderTypes> = new Set<GitProviderTypes>([
  'GitHub',
  ...ALIGNMENT_PROVIDERS
]);

export const providersSupportingVcsRepositoryIntegration: Set<GitProviderTypes> = new Set<GitProviderTypes>([
  'GitLabEnterprise',
  'GitHubEnterprise',
  'BitBucketServer',
  'GitHub',
  ...ALIGNMENT_PROVIDERS
]);

const trackVcsEvent = (event: VcsIntegrationEvent) => {
  track(event.eventName, event.eventProperties); // This is our regular tracking
  track(`${toUpper(event.eventProperties.provider)}_${event.eventName}`); // This event is sent to Hubspot for property updates
};

const trackVcsIntegrationSuccess = (provider: GitProviderTypes) => {
  trackVcsEvent({ eventName: EventNames.VCS_INTEGRATION_SUCCESS, eventProperties: { provider } });
};

// this hook should only hold the state of the VCS step while it's active,
// anything that should persist between steps should be on the `form`
export const useGitProvider = ({ watch, useManualField, setValue }: VCSForm) => {
  const { organizationsStore, blueprintsStore } = useStores();

  const currentOrganizationId = organizationsStore.currentOrganizationId as string;

  const repositories = blueprintsStore.repositoriesByOrganizationId[currentOrganizationId] || [];

  const { tokenId, repository } = watch();
  const [githubInstallationId, setGithubInstallationId] = useManualField<number | null>('githubInstallationId');
  const [isGitLab, setIsGitLab] = useManualField<boolean>('isGitLab');
  const [isAzureDevOps, setIsAzureDevOps] = useManualField<boolean>('isAzureDevOps');
  const [isHelmRepository, setIsHelmRepository] = useManualField<boolean>('isHelmRepository');
  const [bitbucketClientKey, setBitbucketClientKey] = useManualField<string | null>('bitbucketClientKey');
  const [isBitbucketServer, setIsBitbucketServer] = useManualField<boolean>('isBitbucketServer');
  const [isGitLabEnterprise, setIsGitLabEnterprise] = useManualField<boolean>('isGitLabEnterprise');
  const [isGitHubEnterprise, setIsGitHubEnterprise] = useManualField<boolean>('isGitHubEnterprise');
  const [isFormBlocked, setIsFormBlocked] = useState(false);
  const [isLoadingRepositories, setIsLoadingRepositories] = useState(false);
  const [, setVcsConnectionId] = useManualField<string | null>('vcsConnectionId');
  const [gitProvider, setGitProvider] = useState<GitProviderTypes | undefined>(
    getInitialGitProvider({
      repository,
      githubInstallationId,
      isGitLab,
      isAzureDevOps,
      isHelmRepository,
      bitbucketClientKey,
      isBitbucketServer,
      isGitLabEnterprise,
      isGitHubEnterprise
    })
  );

  const fetchRepositories = useCallback(
    async (gitProvider: GitProviderTypes, token?: any) => {
      setIsLoadingRepositories(true);
      try {
        if (gitProvider === 'GitLab' && token) {
          await blueprintsStore.getGitlabRepositories(token);
        } else if (gitProvider === 'BitBucket') {
          await blueprintsStore.getBitbucketRepositories(token);
        } else if (gitProvider === 'AzureDevOps' && token) {
          await blueprintsStore.getAzureDevOpsRepositories(token);
        }
      } finally {
        setIsLoadingRepositories(false);
      }
    },
    [blueprintsStore]
  );

  useAsyncPromise(async () => {
    if (gitProvider) {
      setIsFormBlocked(false);
      await fetchRepositories(gitProvider, isGitProviderUsingToken(gitProvider) ? tokenId : undefined);
    }
  }, [fetchRepositories, gitProvider, tokenId]);

  const resetVCSData = useCallback(
    (type: GitProviderTypes) => {
      setValue('path', '');
      setValue('repository', '', { shouldValidate: false });
      setValue('revision', '', { shouldValidate: false });
      setVcsConnectionId(null);

      blueprintsStore.setRepositories([], currentOrganizationId);

      if (type !== 'GitHub') setGithubInstallationId(null);
      if (type !== 'BitBucket') setBitbucketClientKey(null);

      // Reset the token data in any case
      setValue('tokenName', '');
      setValue('tokenId', '');

      setIsBitbucketServer(type === 'BitBucketServer');
      setIsGitLab(type === 'GitLab');
      setIsAzureDevOps(type === 'AzureDevOps');
      setIsHelmRepository(type === 'HelmRepository');
      setIsGitLabEnterprise(type === 'GitLabEnterprise');
      setIsGitHubEnterprise(type === 'GitHubEnterprise');
    },
    // From some reason all the setters function reference are changing and causing onSuccess ref to change => auth popup to jump twice. So I removed it from the deps array for now.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setValue, blueprintsStore, currentOrganizationId]
  );

  const onClickGitProvider = useCallback(
    async (type: GitProviderTypes) => {
      setGitProvider(type);
      resetVCSData(type);
      trackVcsIntegrationSuccess(type);
    },
    [resetVCSData]
  );

  return {
    gitProvider,
    isFormBlocked,
    isLoadingRepositories,
    repositories,
    onClickGitProvider
  };
};
